// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A timeline visualization component for asset project history.
 * This module provides an interactive timeline view of an asset's
 * project history, including assignments, intervals, and current
 * status. It supports tooltips, scrolling, and dynamic resizing.
 *
 * Key features:
 * - Interactive timeline
 * - Project history segments
 * - Dynamic density scaling
 * - Tooltip information
 * - Scroll synchronization
 */
import { AssetProjectHistorySegment } from '@/asset/api/get-asset-project-history';
import { Asset } from '@/asset/api/types';
import { AssetAssignmentTimelineSegment } from '@/asset/components/asset-assignment-timeline-segment';
import { AssetAssignmentTimelineTooltip } from '@/asset/components/asset-assignment-timeline-tooltip';
import { TimelineAxis, TimelineAxisDensity } from '@/components/timeline-axis';
import { TimelineColumns } from '@/components/timeline-columns';
import { TimelineDateIndicator } from '@/components/timeline-date-indicator';
import { TimelineProvider } from '@/contexts/timeline-provider';
import { useResizeObserver } from '@/hooks/use-resize-observer';
import { Box, alpha, colors, useTheme } from '@mui/material';
import localPoint from '@visx/event/lib/localPointGeneric';
import { scaleTime } from '@visx/scale';
import { defaultStyles, useTooltip, useTooltipInPortal } from '@visx/tooltip';
import { DateTime, Interval } from 'luxon';
import {
  Fragment,
  MouseEvent,
  MouseEventHandler,
  TouchEvent,
  useEffect,
  useRef,
} from 'react';

export const HISTORY_SEGMENT_INPUT_ID = 'input';

// Constants for timeline layout and styling
const AXIS_HEIGHT = 40;
const UNIT_WIDTH = 100;
const BAR_MARGIN = 8;
const BAR_HEIGHT = 30;
const HIGHLIGHT_HEIGHT = BAR_HEIGHT + BAR_MARGIN;
const HIGHLIGHT_OFFSET = BAR_MARGIN * 0.5;

/**
 * Determines the appropriate time scale density based on
 * the interval duration and available width.
 *
 * @param interval - The time interval to display
 * @param width - Available width in pixels
 * @returns The appropriate density for the timeline
 */
function getDensity(interval: Interval, width: number): TimelineAxisDensity {
  const dayCount = interval.length('days');

  if (width / dayCount >= UNIT_WIDTH) {
    return 'day';
  }

  const weekCount = interval.length('weeks');

  if (width / weekCount >= UNIT_WIDTH) {
    return 'week';
  }

  return 'month';
}

/**
 * Props for configuring the AssetProjectHistoryTimeline component.
 *
 * @interface Props
 */
interface Props {
  /**
   * Asset information for the timeline.
   * Only key and serial are required.
   */
  asset: Pick<Asset, 'key' | 'serial'>;

  /**
   * Array of history segments to display.
   * Each segment represents a period in the asset's history.
   */
  history: AssetProjectHistorySegment[];

  /**
   * Time interval to display in the timeline.
   * Determines the visible range.
   */
  interval: Interval;

  /**
   * Optional minimum height for the timeline.
   * Defaults to content height if not specified.
   */
  minimumHeight?: number;

  /**
   * Optional maximum height for the timeline.
   * Defaults to content height if not specified.
   */
  maximumHeight?: number;
}

/**
 * A timeline component for visualizing asset project history.
 * Displays a chronological view of project segments with
 * interactive features like tooltips and scrolling. Automatically
 * adjusts density based on the time range and available space.
 *
 * Features:
 * - Interactive timeline visualization
 * - Dynamic time scale density
 * - Segment highlighting
 * - Tooltip information
 * - Scroll synchronization
 * - Responsive layout
 *
 * Usage example:
 * ```tsx
 * function AssetHistory({ asset }: Props) {
 *   const history = useAssetProjectHistory(asset.key);
 *   const interval = Interval.fromDateTimes(
 *     DateTime.now().minus({ months: 6 }),
 *     DateTime.now()
 *   );
 *
 *   return (
 *     <AssetProjectHistoryTimeline
 *       asset={asset}
 *       history={history}
 *       interval={interval}
 *       minimumHeight={200}
 *     />
 *   );
 * }
 * ```
 */
export function AssetProjectHistoryTimeline(props: Props) {
  const today = DateTime.now().startOf('day');
  const theme = useTheme();
  const svgRef = useRef<SVGSVGElement>(null);
  const ref = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const resizeObserver = useResizeObserver(ref);

  const {
    tooltipTop = 0,
    tooltipLeft = 0,
    tooltipData,
    tooltipOpen,
    showTooltip,
    hideTooltip,
  } = useTooltip<AssetProjectHistorySegment>();

  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    detectBounds: true,
    scroll: true,
  });

  const history = props.history.filter((segment) => {
    if (props.interval.contains(segment.start)) {
      return true;
    }

    if (segment.end === null) {
      return false;
    }

    return props.interval.contains(segment.end);
  });

  useEffect(() => {
    if (scrollContainerRef.current === null) {
      return;
    }

    const index = history.findIndex(
      ({ id }) => id === HISTORY_SEGMENT_INPUT_ID
    );

    const y = index * (BAR_HEIGHT + BAR_MARGIN);

    scrollContainerRef.current.scrollTo({ top: y, behavior: 'smooth' });
  }, [history]);

  const density = getDensity(props.interval, resizeObserver.width);
  const height = Math.max(
    resizeObserver.height - AXIS_HEIGHT,
    BAR_MARGIN + history.length * (BAR_HEIGHT + BAR_MARGIN)
  );

  const dateScale = scaleTime({
    range: [40, resizeObserver.width - 40],
    domain: [props.interval.start.toJSDate(), props.interval.end.toJSDate()],
  });

  /**
   * Handles scroll container reference assignment.
   * Combines tooltip portal and scroll container refs.
   */
  const handleScrollContainerRef = (element: HTMLDivElement | null) => {
    containerRef(element);
    scrollContainerRef.current = element;
  };

  /**
   * Updates tooltip position and content based on mouse/touch event.
   * Calculates the appropriate segment and position for the tooltip.
   */
  const updateTooltip = (
    event: MouseEvent<SVGPathElement> | TouchEvent<SVGPathElement>
  ) => {
    if (svgRef.current === null) {
      return;
    }

    const point = localPoint(svgRef.current, event);

    if (point === null) {
      return;
    }

    const date = dateScale.invert(point.x);

    const index = history.findIndex((segment) => {
      const dateTime = DateTime.fromJSDate(date);
      const segmentInterval = Interval.fromDateTimes(
        segment.start,
        segment.end ?? props.interval.end
      );

      return segmentInterval.contains(dateTime);
    });

    if (index === -1) {
      return;
    }

    const segment = history[index];
    const y = index * (BAR_HEIGHT + BAR_MARGIN) + BAR_MARGIN;

    return showTooltip({
      tooltipTop: y,
      tooltipLeft: point.x,
      tooltipData: segment,
    });
  };

  /**
   * Handles mouse movement over the timeline.
   * Updates tooltip position and content.
   */
  const handleMouseMove: MouseEventHandler<SVGPathElement> = (event) => {
    updateTooltip(event);
  };

  /**
   * Handles mouse leaving the timeline.
   * Hides the tooltip.
   */
  const handleMouseLeave = () => {
    hideTooltip();
  };

  return (
    <Box
      ref={ref}
      flex={1}
      display="flex"
      overflow="hidden"
      flexDirection="column"
    >
      <svg width="100%" height={AXIS_HEIGHT} style={{ display: 'block' }}>
        <TimelineAxis
          scale={dateScale}
          width={resizeObserver.width}
          density={density}
          interval={props.interval}
          axisHeight={AXIS_HEIGHT}
          chartHeight={AXIS_HEIGHT}
        />
      </svg>
      <Box
        ref={handleScrollContainerRef}
        flex={1}
        display="flex"
        position="relative"
        minHeight={props.minimumHeight}
        maxHeight={props.maximumHeight}
        sx={{
          overflowY: 'auto',
          opacity: Math.min(resizeObserver.width, 1),
          transition: theme.transitions.create('opacity'),
        }}
      >
        <svg
          ref={svgRef}
          width="100%"
          height={height}
          style={{ display: 'block' }}
        >
          <TimelineProvider>
            <TimelineColumns
              y={0}
              scale={dateScale}
              height={height}
              density={density}
              interval={props.interval}
            />
            {tooltipOpen && tooltipData && (
              <TooltipInPortal
                top={tooltipTop}
                left={tooltipLeft}
                style={{
                  ...defaultStyles,
                  padding: 0,
                  zIndex: theme.zIndex.tooltip,
                  color: theme.palette.text.primary,
                }}
              >
                <AssetAssignmentTimelineTooltip
                  serial={props.asset.serial}
                  segment={tooltipData}
                />
              </TooltipInPortal>
            )}

            {history.map((segment, index, segments) => {
              const y = index * (BAR_HEIGHT + BAR_MARGIN) + BAR_MARGIN;
              const highlightY = y - HIGHLIGHT_OFFSET;
              const isInput = segment.id === HISTORY_SEGMENT_INPUT_ID;
              const isInvalid = segments.some((other) => {
                if (segment.id === other.id) {
                  return false;
                }

                const segmentInterval = Interval.fromDateTimes(
                  segment.start,
                  segment.end ?? props.interval.end
                );

                const otherInterval = Interval.fromDateTimes(
                  other.start,
                  other.end ?? props.interval.end
                );

                return segmentInterval.overlaps(otherInterval);
              });

              return (
                <Fragment key={segment.id}>
                  {isInput && (
                    <rect
                      x={0}
                      y={highlightY}
                      width={resizeObserver.width}
                      height={HIGHLIGHT_HEIGHT}
                      fill={alpha(colors.yellow[200], 0.5)}
                    />
                  )}
                  <AssetAssignmentTimelineSegment
                    y={y}
                    scale={dateScale}
                    asset={props.asset}
                    height={BAR_HEIGHT}
                    segment={segment}
                    isInvalid={isInvalid}
                    onMouseMove={handleMouseMove}
                    onMouseLeave={handleMouseLeave}
                  />
                </Fragment>
              );
            })}
            <TimelineDateIndicator
              y={0}
              height={height}
              scale={dateScale}
              dateTime={today}
              color={theme.palette.primary.light}
            />
          </TimelineProvider>
        </svg>
      </Box>
    </Box>
  );
}
